import { useEffect, useMemo } from 'react';
import { useApiUrl } from '../../../../global/config/useApiUrl';
import { useCampfireFetch } from '../../../../global/network/useCampfireFetch';
import { RosterListItem } from '../main-section/roster/roster-types';

interface RosterStatusRequest {
  activityId: string;
  activityDate: string;
  activityStatus: string;
}

interface RosterStatusResponse {
  activityId: string;
  activityDate: string;
  status: string;
}

interface BatchRosterStatusResponse {
  statuses: RosterStatusResponse[];
}

export function useBatchRosterStatus(rosters: RosterListItem[], refetchTrigger?: number) {
  const apiUrl = useApiUrl();

  const { run, response, isLoading, error } = useCampfireFetch<BatchRosterStatusResponse>({
    defer: true,
  });

  const rostersKey = rosters.map((r) => `${r.activityId}-${r.activityDate}`).join(',');

  useEffect(() => {
    if (rosters.length === 0) {
      return;
    }

    const rosterRequests: RosterStatusRequest[] = rosters.map((roster) => ({
      activityId: roster.activityId,
      activityDate: roster.activityDate,
      activityStatus: roster.activityStatus,
    }));

    run({
      url: `${apiUrl}/vm/activity/roster/batch-status`,
      method: 'POST',
      data: {
        rosters: rosterRequests,
      },
    });
  }, [rostersKey, apiUrl, refetchTrigger]);

  const data = useMemo(() => {
    if (!response?.data?.data?.statuses) {
      return {};
    }

    const lookup: Record<string, string> = {};
    response.data.data.statuses.forEach((statusItem) => {
      const key = `${statusItem.activityId}-${statusItem.activityDate}`;
      lookup[key] = statusItem.status;
    });

    return lookup;
  }, [response]);

  return { data, isLoading, error };
}
